import React, {useCallback, useEffect, useMemo, useState} from "react";
import {useNavigate} from "react-router-dom";
import {
    Header,
} from "./UserList.styled";
import {ReactComponent as UserPlusIcon} from "../../assets/icons/user-plus.svg";
import {ReactComponent as UserEditIcon} from "../../assets/icons/user-edit.svg";
import {ReactComponent as UserDeleteIcon} from "../../assets/icons/user-minus.svg";
import {Button, Pagination} from "@mui/material";
import axios from "axios";
import {User} from "../../models/models";
import {useSnackbar} from "../../contexts/SnackbarContext";
import ConfirmationDialog from "../../components/dialogs/ConfirmationDialog";
import PageSizeSelector from "../../components/common/PageSizeSelector";
import {parseError} from "../../services/http.service";
import {useTranslation} from 'react-i18next';
import SearchWithTagsInput from "../../components/common/SearchWithTagsInput";
import ResponsiveTable, { ResponsiveTableColumn } from "../../components/common/ResponsiveTable";
import {
    ControlsRow,
    PageSizeWrapper, PaginationWrapper,
    SearchWrapper,
    StyledTableContainer
} from "../../components/common/TableContainer.styled";

export async function fetchUsers(page = 0, size = 10, searchTags: string[]) {
    const params = new URLSearchParams();
    params.append("page", page.toString());
    params.append("size", size.toString());
    searchTags.forEach(tag => params.append('q', tag));

    const result = await axios.get(`/users`, {params});
    return {
        content: result.data.content as User[],
        totalElements: result.data.totalElements || 0,
    };
}

const UserList: React.FC = () => {
    const navigate = useNavigate();
    const {showSnackbar} = useSnackbar();
    const {t} = useTranslation();
    const [users, setUsers] = useState<User[]>([]);
    const [userToDelete, setUserToDelete] = useState<User | null>(null);
    const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [totalElements, setTotalElements] = useState(0);
    const [loading, setLoading] = useState(true);
    const pageSizeOptions = [5, 10, 20, 50];
    const [searchTags, setSearchTags] = useState<string[]>([]);


    const fetchData = useCallback(async () => {
        setLoading(true);
        try {
            const result = await fetchUsers(currentPage - 1, pageSize, searchTags);
            setUsers(result.content);
            setTotalElements(result.totalElements);
        } catch (error) {
            showSnackbar(parseError(error), "error");
            console.error("Błąd pobierania użytkowników:", error);
            setUsers([]);
            setTotalElements(0);
        } finally {
            setLoading(false);
        }
    }, [currentPage, pageSize, searchTags, showSnackbar]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const navigateToEditUser = useCallback((user: User) => {
        const params = {
            ...user,
            createdAt: new Date(user.createdAt).toISOString(),
        } as Record<string, unknown>;
        const usp = new URLSearchParams();
        Object.entries(params).forEach(([key, value]) => {
            if (value !== undefined && value !== null) {
                usp.append(key, String(value));
            }
        });
        navigate(`/admin/user/edit?${usp.toString()}`);
    }, [navigate]);

    const openDeleteDialog = useCallback((user: User) => {
        setUserToDelete(user);
        setIsDeleteDialogOpen(true);
    }, []);

    const confirmDeleteUser = async () => {
        if (!userToDelete) return;
        try {
            await axios.delete('/users', {
                data: {userEmail: userToDelete.email}
            });
            await fetchData();
            showSnackbar("Użytkownik został pomyślnie usunięty.", "success");
        } catch (error) {
            showSnackbar(parseError(error), "error");
            console.error("Błąd usuwania użytkownika:", error);
        } finally {
            setIsDeleteDialogOpen(false);
            setUserToDelete(null);
        }
    };

    const handlePageChange = (event: React.ChangeEvent<unknown>, value: number) => {
        setCurrentPage(value);
    };

    const handlePageSizeChange = (size: number) => {
        setPageSize(size);
        setCurrentPage(1);
    };

    const handleTagsChange = (tags: string[]) => {
        setSearchTags(tags);
        setCurrentPage(1);
    };

    const columns: ResponsiveTableColumn<User>[] = useMemo(() => [
        {
            id: 'email',
            header: t('userList.tableHeaders.email'),
            render: (u) => u.email,
        },
        {
            id: 'role',
            header: t('userList.tableHeaders.role'),
            render: (u) => t(`userList.roleLabels.${u.role}`),
        },
        {
            id: 'active',
            header: t('userList.tableHeaders.active'),
            render: (u) => (u.activated ? t('userList.yes') : t('userList.no')),
        },
        {
            id: 'actions',
            header: t('userList.tableHeaders.actions'),
            cellClassName: 'actions-cell',
            render: (u) => (
                <>
                    <Button 
                        variant={'outlined'} 
                        onClick={() => openDeleteDialog(u)}
                        aria-label={t('userList.deleteButton.ariaLabel', { email: u.email }) as string}
                    >
                        <UserDeleteIcon aria-hidden={true} focusable={false}/>
                        <span style={{marginLeft: '8px'}}>{t('userList.deleteButton')}</span>
                    </Button>
                    <Button 
                        variant={'outlined'} 
                        onClick={() => navigateToEditUser(u)}
                        aria-label={t('userList.editButton.ariaLabel', { email: u.email }) as string}
                    >
                        <UserEditIcon aria-hidden={true} focusable={false}/>
                        <span style={{marginLeft: '8px'}}>{t('userList.editButton')}</span>
                    </Button>
                </>
            ),
        },
    ], [t, openDeleteDialog, navigateToEditUser]);

    return (
        <StyledTableContainer role="region" aria-labelledby="user-list-title">
                <Header>
                    <h2 id="user-list-title">
                        {t('userList.title')}
                    </h2>
                    <Button
                        variant={'contained'}
                        onClick={() => navigate("/admin/user/create")}
                        startIcon={<UserPlusIcon aria-hidden={true} focusable={false}/>} 
                        aria-label={t('userList.addButton.ariaLabel') as string}
                    >
                        {t('userList.addButton')}
                    </Button>
                </Header>

                <ControlsRow>
                    <SearchWrapper>
                        <SearchWithTagsInput 
                            onTagsChange={handleTagsChange} 
                            initialTags={searchTags}
                            ariaLabel={t('userList.search.ariaLabel') as string}
                        />
                    </SearchWrapper>
                    <PageSizeWrapper>
                        <PageSizeSelector
                            options={pageSizeOptions}
                            selectedSize={pageSize}
                            onChange={handlePageSizeChange}
                            ariaLabelGroup={t('pageSizeSelector.aria.group') as string}
                            getSizeAriaLabel={(size) => t('pageSizeSelector.aria.setSize', { size }) as string}
                        />
                    </PageSizeWrapper>
                </ControlsRow>
                <div aria-live="polite" aria-atomic={true} aria-busy={loading}>
                    {loading && (
                        <div role="status" aria-live="polite" style={{ position: 'absolute', width: 1, height: 1, overflow: 'hidden', clip: 'rect(1px, 1px, 1px, 1px)' }}>
                            {t('userList.loading')}
                        </div>
                    )}
                    <ResponsiveTable
                        columns={columns}
                        data={users}
                        getRowKey={(u) => u.email}
                        emptyState={loading ? t('userList.loading') : t('userList.emptyState')}
                        ariaLabel={t('userList.table.ariaLabel') as string}
                    />
                </div>

                {totalElements > pageSize && (
                    <PaginationWrapper>
                        <Pagination
                            count={Math.ceil(totalElements / pageSize)}
                            page={currentPage}
                            onChange={handlePageChange}
                            color="primary"
                            aria-label={t('userList.pagination.ariaLabel') as string}
                        />
                    </PaginationWrapper>
                )}
            {userToDelete && (
                <ConfirmationDialog
                    open={isDeleteDialogOpen}
                    onClose={() => setIsDeleteDialogOpen(false)}
                    onConfirm={confirmDeleteUser}
                    title="Potwierdzenie usunięcia"
                    message={<>Czy na pewno chcesz usunąć użytkownika: &ldquo;{userToDelete.email}&rdquo;?</>}
                    confirmButtonText="Usuń"
                    cancelButtonText="Anuluj"
                />
            )}
        </StyledTableContainer>
    );
};

export default UserList;
